/* import __COLOCATED_TEMPLATE__ from './redact-message-modal.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type Conversation from 'embercom/objects/inbox/conversation';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import { type Block, type Attachment } from '@intercom/interblocks.ts';
import type { Upload } from 'embercom/objects/inbox/renderable/upload';

type AnyAttachment = Upload | Attachment;

interface Signature {
  Args: {
    part: RenderablePart;
    conversation: Conversation;
    onClose: () => void;
    onRedact: (selectedText: string, attachmentIds: number[]) => void;
  };
}

const INLINE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

export default class RedactMessageModal extends Component<Signature> {
  @tracked selectedText = '';
  @tracked selectedAttachmentIds: number[] = [];
  @tracked selectionError = '';
  @tracked matchCount = 0;

  private selectionHandler?: () => void;
  private contentElement?: HTMLElement;

  get canRedact(): boolean {
    return this.selectedText.length > 0 || this.selectedAttachmentIds.length > 0;
  }

  get blocks(): Block[] {
    let renderableData = this.args.part.renderableData as { blocks?: Block[] };
    let blocks = renderableData.blocks ?? [];
    return blocks.filter(
      (block: Block) =>
        block.type === 'paragraph' ||
        block.type === 'orderedList' ||
        block.type === 'unorderedList',
    );
  }

  get attachments(): AnyAttachment[] {
    let renderableData = this.args.part.renderableData as {
      blocks?: Block[];
      uploads?: Upload[];
    };
    let uploads = renderableData.uploads;
    if (uploads && uploads.length > 0) {
      return uploads;
    }

    let blocks = renderableData.blocks ?? [];
    let attachments: AnyAttachment[] = [];
    for (let block of blocks) {
      if (block.type === 'attachmentList') {
        attachments.push(
          ...(block as { type: 'attachmentList'; attachments: Attachment[] }).attachments,
        );
      }
    }
    return attachments;
  }

  get imageAttachments(): AnyAttachment[] {
    return this.attachments.filter((a) => INLINE_IMAGE_TYPES.includes(a.contentType));
  }

  get fileAttachments(): AnyAttachment[] {
    return this.attachments.filter((a) => !INLINE_IMAGE_TYPES.includes(a.contentType));
  }

  get hasBlocks(): boolean {
    return this.blocks.length > 0;
  }

  get hasAttachments(): boolean {
    return this.attachments.length > 0;
  }

  @action setupSelectionListener(element: HTMLElement) {
    this.contentElement = element;
    let handler = this.handleSelectionComplete.bind(this);
    this.selectionHandler = handler;
    element.addEventListener('mouseup', handler);
  }

  @action teardownSelectionListener() {
    if (this.selectionHandler && this.contentElement) {
      this.contentElement.removeEventListener('mouseup', this.selectionHandler);
      this.selectionHandler = undefined;
    }
  }

  @action toggleAttachmentSelection(attachment: AnyAttachment) {
    let id = this.getAttachmentId(attachment);
    let index = this.selectedAttachmentIds.indexOf(id);
    if (index === -1) {
      this.selectedAttachmentIds = [...this.selectedAttachmentIds, id];
    } else {
      this.selectedAttachmentIds = this.selectedAttachmentIds.filter((aid) => aid !== id);
    }
  }

  @action isAttachmentSelected(attachment: AnyAttachment): boolean {
    return this.selectedAttachmentIds.includes(this.getAttachmentId(attachment));
  }

  @action clearSelectedText() {
    this.setSelection('');
  }

  @action confirmRedact() {
    this.args.onRedact(this.selectedText, this.selectedAttachmentIds);
  }

  private handleSelectionComplete() {
    let selection = window.getSelection();
    if (!selection || !this.contentElement) {
      return;
    }

    let text = selection.toString().trim();
    if (text.length === 0 || selection.rangeCount === 0) {
      return;
    }

    this.setSelection(text);
  }

  private setSelection(text: string) {
    this.clearHighlights();
    this.selectionError = '';

    if (text.length > 0) {
      let count = this.highlightPhrase(text);
      if (count > 0) {
        this.selectedText = text;
        this.matchCount = count;
        return;
      }
      this.selectionError = 'selection-failed';
    }

    this.selectedText = '';
    this.matchCount = 0;
  }

  private highlightPhrase(phrase: string): number {
    if (!this.contentElement || phrase.length === 0) {
      return 0;
    }

    let walker = document.createTreeWalker(this.contentElement, NodeFilter.SHOW_TEXT, null);
    let textNodes: Text[] = [];
    let node: Text | null;

    while ((node = walker.nextNode() as Text)) {
      if (node.textContent?.includes(phrase)) {
        textNodes.push(node);
      }
    }

    if (textNodes.length === 0) {
      return 0;
    }

    let totalMatches = 0;

    for (let textNode of textNodes) {
      let content = textNode.textContent;
      let fragment = document.createDocumentFragment();
      let lastIndex = 0;
      let index: number;

      while ((index = content.indexOf(phrase, lastIndex)) !== -1) {
        if (index > lastIndex) {
          fragment.appendChild(document.createTextNode(content.slice(lastIndex, index)));
        }
        let mark = document.createElement('mark');
        mark.style.backgroundColor = 'var(--error-container)';
        mark.style.cursor = 'text';
        mark.textContent = phrase;
        fragment.appendChild(mark);
        lastIndex = index + phrase.length;
        totalMatches++;
      }

      if (lastIndex < content.length) {
        fragment.appendChild(document.createTextNode(content.slice(lastIndex)));
      }

      textNode.replaceWith(fragment);
    }

    return totalMatches;
  }

  private clearHighlights() {
    if (!this.contentElement) {
      return;
    }

    for (let mark of [...this.contentElement.querySelectorAll('mark')]) {
      mark.replaceWith(mark.textContent ?? '');
    }
    this.contentElement.normalize();
  }

  private getAttachmentId(attachment: AnyAttachment): number {
    if ('id' in attachment) {
      return attachment.id;
    }
    return (attachment as Attachment & { attachmentId: number }).attachmentId;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::PartGroups::RedactMessageModal': typeof RedactMessageModal;
    'inbox2/conversation-stream/part-groups/redact-message-modal': typeof RedactMessageModal;
  }
}
